/**
 * Never email: the company-wide list of addresses that are never sent
 * marketing again, by any brand, and why. People who unsubscribed, bounced,
 * complained, failed verification or never engaged (sunset), plus any
 * added by hand.
 *
 * Addresses are stored as a keyed hash, not in the clear, so the list can't
 * be shown as emails. It can be counted and filtered by reason, and any one
 * address can be looked up or added by typing it.
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, SegmentedControl, SimpleGrid, Stack, Table, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAddEmailSuppression, useCheckEmailSuppression, useEmailSuppressions } from '../../api/queries';
import { formatDateTime, formatNumber, TOKENS } from '../../theme';
import { reasonInfo } from './reasons';
import { SunsetCard } from './SunsetCard';

function when(raw: string) {
  return raw ? formatDateTime(raw.replace(' ', 'T') + 'Z') : '';
}

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'unsubscribe', label: 'Unsubscribed' },
  { value: 'bounce', label: 'Bounced' },
  { value: 'complaint', label: 'Spam' },
  { value: 'invalid', label: 'Failed verification' },
  { value: 'sunset', label: 'Never engaged' },
  { value: 'manual', label: 'By hand' },
];

export function UnsubscribesTab() {
  const [reason, setReason] = useState('');
  const list = useEmailSuppressions(reason || undefined);
  const check = useCheckEmailSuppression();
  const add = useAddEmailSuppression();
  const [email, setEmail] = useState('');
  const d = list.data;
  const counts: [string, string, number | undefined][] = [
    ['unsubscribe', 'Unsubscribed', d?.unsubscribes],
    ['bounce', 'Bounced', d?.bounces],
    ['complaint', 'Marked as spam', d?.complaints],
    ['invalid', 'Failed verification', d?.invalid],
    ['sunset', 'Never engaged', d?.sunset],
    ['manual', 'Added by hand', d?.manual],
  ];

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" maw={680}>
        Anyone here is never sent marketing email again, by Swicc, Utility Switch or Savvy Switch.
        Addresses are stored scrambled, so they can be looked up but not listed.
      </Text>
      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
        {counts.map(([key, label, n]) => (
          <UnstyledButton key={key} onClick={() => setReason(reason === key ? '' : key)} aria-pressed={reason === key}>
            <Card withBorder radius="md" padding="md"
              style={reason === key ? { borderColor: TOKENS.text, borderWidth: 1.5 } : undefined}>
              <Text size="sm" c="dimmed">{label}</Text>
              <Text fz={24} fw={700}>{formatNumber(n ?? 0)}</Text>
            </Card>
          </UnstyledButton>
        ))}
      </SimpleGrid>

      <Card withBorder radius="md" padding="md">
        <Text size="sm" fw={600} mb={6}>Look up or add an address</Text>
        <Group align="flex-end">
          <TextInput style={{ flex: 1 }} placeholder="name@example.com" value={email}
            onChange={(e) => { const v = e.currentTarget.value; setEmail(v); check.reset(); }} />
          <Button variant="default" loading={check.isPending} disabled={!email.includes('@')} onClick={() => check.mutate(email)}>Look up</Button>
          <Button color="red" variant="light" loading={add.isPending} disabled={!email.includes('@')} onClick={() => add.mutate(email, {
            onSuccess: () => { notifications.show({ color: 'teal', title: 'Added', message: `${email} won't be emailed again.` }); check.mutate(email); },
            onError: (e) => notifications.show({ color: 'red', title: 'Not added', message: (e as Error).message }),
          })}>Never email</Button>
        </Group>
        {check.data && (
          <Text size="sm" mt="xs">
            {check.data.suppressed
              ? <>On the list: <b>{reasonInfo(check.data.reason).label}</b>{reasonInfo(check.data.reason).detail ? ` (${reasonInfo(check.data.reason).detail})` : ''}, {when(check.data.created_at)}.</>
              : 'Not on the list. Campaigns can email this address if it opted in.'}
          </Text>
        )}
      </Card>

      <Stack gap={6}>
        <Group justify="space-between" wrap="wrap">
          <Text size="sm" fw={600}>Most recent</Text>
          <SegmentedControl size="xs" value={reason} onChange={setReason} data={FILTERS} aria-label="Filter by reason" />
        </Group>
        <Table.ScrollContainer minWidth={520}>
          <Table verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr><Table.Th>When</Table.Th><Table.Th>Why</Table.Th><Table.Th>From campaign</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {d && d.recent.length === 0 && (
                <Table.Tr><Table.Td colSpan={3}><Text size="sm" c="dimmed" ta="center" py="md">Nobody{reason ? ' for this reason' : ''} yet.</Text></Table.Td></Table.Tr>
              )}
              {d?.recent.map((r, i) => {
                const info = reasonInfo(r.reason);
                return (
                  <Table.Tr key={i}>
                    <Table.Td><Text size="sm">{when(r.created_at)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge variant="light" color={info.color}>{info.label}</Badge>
                        {info.detail && <Text size="xs" c="dimmed">{info.detail}</Text>}
                      </Group>
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{r.campaign_name || '-'}</Text></Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>

      <SunsetCard />
    </Stack>
  );
}
